import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Chip from '@material-ui/core/Chip';
import RepeatIcon from '@material-ui/icons/Repeat';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { isNil } from "lodash";
import { i18n } from "../../translate/i18n";
import moment from "moment";
import "./styles.css";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  FormControlLabel,
  Switch,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
  Card,
  CardContent,
  Checkbox,
  FormGroup,
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import UserStatusIcon from "../UserModal/statusIcon";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import useQueues from "../../hooks/useQueues";

// Component to handle preview updates efficiently
const PreviewUpdater = ({ values, onPreviewUpdate }) => {
  const prevValuesRef = useRef(null);

  useEffect(() => {
    const currentMessages = {
      message1: values.message1 || "",
      message2: values.message2 || "",
      message3: values.message3 || "",
      message4: values.message4 || "",
      message5: values.message5 || "",
    };

    // Only call callback if messages have changed
    if (
      !prevValuesRef.current ||
      prevValuesRef.current.message1 !== currentMessages.message1 ||
      prevValuesRef.current.message2 !== currentMessages.message2 ||
      prevValuesRef.current.message3 !== currentMessages.message3 ||
      prevValuesRef.current.message4 !== currentMessages.message4 ||
      prevValuesRef.current.message5 !== currentMessages.message5
    ) {
      prevValuesRef.current = currentMessages;
      onPreviewUpdate(currentMessages);
    }
  }, [values.message1, values.message2, values.message3, values.message4, values.message5, onPreviewUpdate]);

  return null;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  recurrenceCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  previewCard: {
    marginTop: theme.spacing(1),
    maxHeight: 200,
    overflow: 'auto',
  },
  recurrenceIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const CampaignSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Obrigatório"),
  scheduledAt: Yup.string()
    .nullable()
    .required("Agendamento é obrigatório"),
  isRecurring: Yup.boolean().default(false),
  recurrenceType: Yup.string().when('isRecurring', {
    is: true,
    then: Yup.string().oneOf(['minutely', 'hourly', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly']).required('Tipo de recorrência é obrigatório'),
    otherwise: Yup.string().nullable()
  }),
  recurrenceInterval: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Intervalo deve ser maior que 0').required('Intervalo é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  recurrenceDaysOfWeek: Yup.array().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'weekly',
    then: Yup.array().min(1, 'Selecione pelo menos um dia da semana').required(),
    otherwise: Yup.array().nullable()
  }),
  recurrenceDayOfMonth: Yup.number().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'monthly',
    then: Yup.number().min(1, 'Dia deve ser entre 1 e 31').max(31, 'Dia deve ser entre 1 e 31').required('Dia do mês é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  recurrenceEndDate: Yup.date().when('isRecurring', {
    is: true,
    then: Yup.date().min(new Date(), 'Data final deve ser futura').nullable(),
    otherwise: Yup.date().nullable()
  }),
  maxExecutions: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Número máximo deve ser maior que 0').nullable(),
    otherwise: Yup.number().nullable()
  })
});

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
  templateConfig,
  isPage = false,
  onPreviewUpdate = null,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user, socket } = useContext(AuthContext);
  const { companyId } = user;

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA",
    confirmation: false,
    scheduledAt: "",
    contactListId: "",
    tagListId: "Nenhuma",
    companyId,
    statusTicket: "closed",
    openTicket: "disabled",
    isRecurring: false,
    recurrenceType: "",
    recurrenceInterval: 1,
    recurrenceDaysOfWeek: [],
    recurrenceDayOfMonth: 1,
    recurrenceEndDate: "",
    maxExecutions: null,
    maxContacts: null,
    intervalBetweenMessages: 120,
    delayBetweenContacts: 60,
    initialDelay: 0,
    useAI: false,
    aiProvider: "groq",
    aiPrompt: "",
    humanized: false,
    riskLevel: null,
    apiType: null,
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
  const [whatsappId, setWhatsappId] = useState(null);
  const [contactLists, setContactLists] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const [previewExecutions, setPreviewExecutions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const attachmentFile = useRef(null);
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const { findAll: findAllQueues } = useQueues();

  const daysOfWeekOptions = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
  ];

  const handlePreviewRecurrence = async (values) => {
    if (!values.isRecurring || !values.recurrenceType || !values.scheduledAt) {
      setPreviewExecutions([]);
      return;
    }

    try {
      const params = {
        recurrenceType: values.recurrenceType,
        recurrenceInterval: values.recurrenceInterval,
        recurrenceDaysOfWeek: JSON.stringify(values.recurrenceDaysOfWeek),
        recurrenceDayOfMonth: values.recurrenceDayOfMonth,
      };

      if (campaignId) {
        const { data } = await api.get(`/campaigns/${campaignId}/recurrence-preview`, { params });
        setPreviewExecutions(data.executions);
      } else {
        const mockExecutions = calculateMockExecutions(values);
        setPreviewExecutions(mockExecutions);
      }
    } catch (err) {
      console.error('Erro ao buscar preview:', err);
    }
  };

  const calculateMockExecutions = (values) => {
    const executions = [];
    let currentDate = moment(values.scheduledAt);
    
    for (let i = 0; i < 5; i++) {
      executions.push(currentDate.format('DD/MM/YYYY HH:mm'));
      
      switch (values.recurrenceType) {
        case 'minutely':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'minutes');
          break;
        case 'hourly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'hours');
          break;
        case 'daily':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'days');
          break;
        case 'weekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'weeks');
          break;
        case 'biweekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval * 2, 'weeks');
          break;
        case 'monthly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'months');
          if (values.recurrenceDayOfMonth) {
            currentDate = currentDate.date(values.recurrenceDayOfMonth);
          }
          break;
        case 'yearly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'years');
          break;
      }
    }
    
    return executions;
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Preview updates are now handled inside Formik render for isPage mode

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      api
        .get(`/contact-lists/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api
        .get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          const mappedWhatsapps = data.map((whatsapp) => ({
            ...whatsapp,
            selected: false,
          }));
          setWhatsapps(mappedWhatsapps);
        });

      api.get(`/tags/list`, { params: { companyId, kanban: 0 } })
        .then(({ data }) => {
          const fetchedTags = data;
          const formattedTagLists = fetchedTags
            .filter(tag => tag.contacts.length > 0)
            .map((tag) => ({
              id: tag.id,
              name: `${tag.name} (${tag.contacts.length})`,
            }));
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("Error retrieving tags:", error);
        });

      if (!campaignId) return;

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {
        if (data?.user) setSelectedUser(data.user);
        if (data?.queue) setSelectedQueue(data.queue.id);
        if (data?.whatsappId) {
          setWhatsappId(parseInt(data.whatsappId));
        } else {
          setWhatsappId(null);
        }
        
        setCampaign((prev) => {
          let prevCampaignData = Object.assign({}, prev);

          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value !== "" && value !== null) {
              prevCampaignData[key] = moment(value).format("YYYY-MM-DDTHH:mm");
            } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
              prevCampaignData[key] = moment(value).format("YYYY-MM-DD");
            } else if (key === "recurrenceDaysOfWeek" && value) {
              prevCampaignData[key] = JSON.parse(value);
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });

          return prevCampaignData;
        });
      });
    }
  }, [campaignId, open, initialValues, companyId]);

  useEffect(() => {
    if (templateConfig && open) {
      setCampaign(prevCampaign => ({
        ...prevCampaign,
        name: "",
        message1: templateConfig.message1 || "",
        message2: templateConfig.message2 || "",
        message3: templateConfig.message3 || "",
        message4: templateConfig.message4 || "",
        message5: templateConfig.message5 || "",
        confirmation: templateConfig.confirmation || false,
        confirmationMessage1: templateConfig.confirmationMessage1 || "",
        confirmationMessage2: templateConfig.confirmationMessage2 || "",
        confirmationMessage3: templateConfig.confirmationMessage3 || "",
        confirmationMessage4: templateConfig.confirmationMessage4 || "",
        confirmationMessage5: templateConfig.confirmationMessage5 || "",
        maxContacts: templateConfig.maxContacts || null,
        intervalBetweenMessages: templateConfig.intervalBetweenMessages || null,
        delayBetweenContacts: templateConfig.delayBetweenContacts || null,
        humanized: templateConfig.humanized || false,
        riskLevel: templateConfig.riskLevel || null,
        apiType: templateConfig.apiType || null,
      }));
    }
  }, [templateConfig, open]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThenAnHour =
      !Number.isNaN(scheduledAt.diff(now)) && scheduledAt.diff(now, "hour") > 1;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && moreThenAnHour);

    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    try {
      if (onClose && typeof onClose === 'function') {
        onClose();
      }
    } catch (err) {
      console.error('Erro ao fechar:', err);
    }
    setCampaign(initialState);
    setPreviewExecutions([]);
    setShowPreview(false);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveCampaign = async (values) => {
    try {
      const dataValues = {
        ...values,
        whatsappId: whatsappId,
        userId: selectedUser?.id || null,
        queueId: selectedQueue || null,
        recurrenceDaysOfWeek: (values.isRecurring && values.recurrenceDaysOfWeek && values.recurrenceDaysOfWeek.length > 0) 
          ? JSON.stringify(values.recurrenceDaysOfWeek)
          : null,
      };

      Object.entries(values).forEach(([key, value]) => {
        if (key === "scheduledAt") {
          if (value !== "" && value !== null && value !== undefined) {
            dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
          }
        } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
          dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
        } else if (key !== "recurrenceDaysOfWeek") {
          dataValues[key] = value === "" ? null : value;
        }
      });

      if (!values.isRecurring) {
        dataValues.recurrenceType = null;
        dataValues.recurrenceInterval = null;
        dataValues.recurrenceDaysOfWeek = null;
        dataValues.recurrenceDayOfMonth = null;
        dataValues.recurrenceEndDate = null;
        dataValues.maxExecutions = null;
      }

      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, dataValues);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${campaignId}/media-upload`, formData);
        }
        handleClose();
      } else {
        const { data } = await api.post("/campaigns", dataValues);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${data.id}/media-upload`, formData);
        }
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  const renderMessageField = (identifier) => {
    return (
      <Field
        as={TextField}
        id={identifier}
        name={identifier}
        fullWidth
        rows={5}
        label={i18n.t(`campaigns.dialog.form.${identifier}`)}
        placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
        multiline={true}
        variant="outlined"
        helperText="Utilize variáveis como {nome}, {numero}, {email} ou defina variáveis personalizadas."
        disabled={!campaignEditable && campaign.status !== "CANCELADA"}
      />
    );
  };

  const renderConfirmationMessageField = (identifier) => {
    return (
      <Field
        as={TextField}
        id={identifier}
        name={identifier}
        fullWidth
        rows={5}
        label={i18n.t(`campaigns.dialog.form.${identifier}`)}
        placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
        multiline={true}
        variant="outlined"
        disabled={!campaignEditable && campaign.status !== "CANCELADA"}
      />
    );
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filterOptions = createFilterOptions({
    trim: true,
  });

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {isPage ? (
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
      ) : null}
      <Dialog
        open={open && !isPage}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <Formik
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <>
              <DialogTitle id="form-dialog-title">
                {campaignEditable ? (
                  <>
                    {campaignId
                      ? `${i18n.t("campaigns.dialog.update")}`
                      : `${i18n.t("campaigns.dialog.new")}`}
                    {values.apiType === "uzap-medium" && " - ⚠️ Uzap API Médio Risco"}
                    {values.apiType === "official-notification-hub" && " - ✅ API Oficial Meta (Notification Hub)"}
                    {values.apiType === "official-waba" && " - ✅ API Oficial Meta (WABA)"}
                    {values.apiType === "humanized" && " - ⚠️ Campanha Humanizada"}
                    {values.apiType === "medium" && " - ⚠️ Campanha Médio Risco"}
                    {values.apiType === "high" && " - ⚠️ Campanha Alto Risco"}
                  </>
                ) : (
                  <>{`${i18n.t("campaigns.dialog.readonly")}`}</>
                )}
              </DialogTitle>
              <Form>
                <DialogContent dividers>
                  <Grid spacing={2} container>
                    {values.apiType && (
                      <Grid xs={12} item>
                        <Box style={{
                          background: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#dbeafe" : "#fee2e2",
                          border: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "2px solid #0284c7" : "2px solid #dc2626",
                          borderRadius: "8px",
                          padding: "20px",
                          marginBottom: "16px"
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography style={{ 
                              color: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#0c4a6e" : "#991b1b", 
                              fontWeight: 700, 
                              fontSize: "16px" 
                            }}>
                              {(values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "✅ API OFICIAL META" : "⚠️ API NÃO OFICIAL"}
                            </Typography>
                            <Box style={{
                              background: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#0284c7" : "#dc2626",
                              color: "white",
                              padding: "8px 16px",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: 600
                            }}>
                              {values.apiType === "uzap-medium" && "Uzap API - Médio Risco"}
                              {values.apiType === "official-notification-hub" && "Notification Hub"}
                              {values.apiType === "official-waba" && "WABA"}
                              {values.apiType === "humanized" && "Campanha Humanizada"}
                              {values.apiType === "medium" && "Campanha Médio Risco"}
                              {values.apiType === "high" && "Campanha Alto Risco"}
                            </Box>
                          </Box>
                          
                          <Typography style={{ 
                            color: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#164e63" : "#7f1d1d", 
                            fontSize: "13px", 
                            lineHeight: "1.6" 
                          }}>
                            {values.apiType === "uzap-medium" && "Você está usando a Uzap API (Não-Oficial). Existe risco de bloqueio e perda do número de WhatsApp. Use com cautela e sempre respeite os limites recomendados."}
                            {values.apiType === "humanized" && "Você está usando a Campanha Humanizada (Não-Oficial). Envios lentos e naturais para evitar bloqueios. Ideal para campanhas de relacionamento."}
                            {values.apiType === "medium" && "Você está usando a Campanha Médio Risco (Não-Oficial). Envios moderados com bom alcance. Equilibra velocidade e segurança."}
                            {values.apiType === "high" && "Você está usando a Campanha Alto Risco (Não-Oficial). Envios rápidos para máximo alcance. Maior velocidade, maior risco de bloqueio."}
                            {values.apiType === "official-notification-hub" && "Você está usando a WhatsApp API Oficial Meta via Notification Hub. Esta é a forma mais segura e confiável. Você será cobrado por mensagem enviada."}
                            {values.apiType === "official-waba" && "Você está usando a WhatsApp API Oficial Meta via WABA. Esta é a forma mais segura e confiável. Você será cobrado por mensagem enviada."}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    <Grid xs={12} md={6} item>
                      <Field
                        as={TextField}
                        label={i18n.t("campaigns.dialog.form.name")}
                        name="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.textField}
                        disabled={!campaignEditable}
                      />
                    </Grid>
                    
                    <Grid xs={12} md={6} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="confirmation-selection-label">
                          {i18n.t("campaigns.dialog.form.confirmation")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("campaigns.dialog.form.confirmation")}
                          placeholder={i18n.t("campaigns.dialog.form.confirmation")}
                          labelId="confirmation-selection-label"
                          id="confirmation"
                          name="confirmation"
                          error={touched.confirmation && Boolean(errors.confirmation)}
                          disabled={!campaignEditable}
                        >
                          <MenuItem value={false}>Desabilitada</MenuItem>
                          <MenuItem value={true}>Habilitada</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} md={6} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="contactList-selection-label">
                          {i18n.t("campaigns.dialog.form.contactList")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("campaigns.dialog.form.contactList")}
                          placeholder={i18n.t("campaigns.dialog.form.contactList")}
                          labelId="contactList-selection-label"
                          id="contactListId"
                          name="contactListId"
                          error={touched.contactListId && Boolean(errors.contactListId)}
                          disabled={!campaignEditable}
                        >
                          <MenuItem value="">Nenhuma</MenuItem>
                          {contactLists &&
                            contactLists.map((contactList) => (
                              <MenuItem key={contactList.id} value={contactList.id}>
                                {contactList.name}
                              </MenuItem>
                            ))}
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} md={6} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="tagList-selection-label">
                          {i18n.t("campaigns.dialog.form.tagList")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("campaigns.dialog.form.tagList")}
                          placeholder={i18n.t("campaigns.dialog.form.tagList")}
                          labelId="tagList-selection-label"
                          id="tagListId"
                          name="tagListId"
                          error={touched.tagListId && Boolean(errors.tagListId)}
                          disabled={!campaignEditable}
                        >
                          {Array.isArray(tagLists) &&
                            tagLists.map((tagList) => (
                              <MenuItem key={tagList.id} value={tagList.id}>
                                {tagList.name}
                              </MenuItem>
                            ))}
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} md={6} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="whatsapp-selection-label">
                          {i18n.t("campaigns.dialog.form.whatsapp")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("campaigns.dialog.form.whatsapp")}
                          placeholder={i18n.t("campaigns.dialog.form.whatsapp")}
                          labelId="whatsapp-selection-label"
                          id="whatsappIds"
                          name="whatsappIds"
                          required
                          error={touched.whatsappId && Boolean(errors.whatsappId)}
                          disabled={!campaignEditable}
                          value={whatsappId}
                          onChange={(event) => {
                            setWhatsappId(event.target.value);
                            setFieldValue('whatsappId', event.target.value);
                          }}
                        >
                          {whatsapps &&
                            whatsapps.map((whatsapp) => (
                              <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                {whatsapp.name}
                              </MenuItem>
                            ))}
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} md={6} item>
                      <Field
                        as={TextField}
                        label={i18n.t("campaigns.dialog.form.scheduledAt")}
                        name="scheduledAt"
                        error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                        helperText={touched.scheduledAt && errors.scheduledAt}
                        variant="outlined"
                        margin="dense"
                        type="datetime-local"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        className={classes.textField}
                        disabled={!campaignEditable}
                      />
                    </Grid>

                    {(values.maxContacts || values.intervalBetweenMessages || values.delayBetweenContacts) && (
                      <Grid xs={12} item>
                        <Card className={classes.recurrenceCard}>
                          <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                              <Typography variant="h6">⚙️ Configuração de Campanha</Typography>
                            </Box>
                            <Box style={{
                              background: "#fef3c7",
                              border: "1px solid #fcd34d",
                              borderRadius: "6px",
                              padding: "12px",
                              marginBottom: "16px"
                            }}>
                              <Typography style={{ fontSize: "13px", color: "#92400e", lineHeight: "1.5" }}>
                                💡 <strong>Dica:</strong> Se o número de contatos exceder o limite configurado, a campanha será automaticamente dividida em múltiplas campanhas para respeitar os limites de segurança.
                              </Typography>
                            </Box>
                            <Grid spacing={2} container>
                              {values.maxContacts && (
                                <Grid xs={12} md={4} item>
                                  <Field
                                    as={TextField}
                                    label="Máximo de Contatos"
                                    name="maxContacts"
                                    type="number"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    disabled={!campaignEditable}
                                  />
                                </Grid>
                              )}
                              {values.intervalBetweenMessages !== null && (
                                <Grid xs={12} md={4} item>
                                  <Field
                                    as={TextField}
                                    label="Intervalo entre Mensagens (minutos)"
                                    name="intervalBetweenMessages"
                                    type="number"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    disabled={!campaignEditable}
                                  />
                                </Grid>
                              )}
                              {values.delayBetweenContacts !== null && (
                                <Grid xs={12} md={4} item>
                                  <Field
                                    as={TextField}
                                    label="Delay entre Contatos (segundos)"
                                    name="delayBetweenContacts"
                                    type="number"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    disabled={!campaignEditable}
                                  />
                                </Grid>
                              )}
                              <Grid xs={12} md={4} item>
                                <Field
                                  as={TextField}
                                  label="Delay Inicial (segundos)"
                                  name="initialDelay"
                                  type="number"
                                  variant="outlined"
                                  margin="normal"
                                  fullWidth
                                  disabled={!campaignEditable}
                                  helperText="Aguarde antes de iniciar"
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    <Grid xs={12} item>
                      <Card className={classes.recurrenceCard}>
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={2}>
                            <Typography variant="h6">🤖 Usar Inteligência Artificial</Typography>
                          </Box>
                          <Grid spacing={2} container>
                            <Grid xs={12} md={6} item>
                              <FormControlLabel
                                control={
                                  <Field
                                    as={Switch}
                                    name="useAI"
                                    color="primary"
                                    disabled={!campaignEditable}
                                  />
                                }
                                label="Habilitar IA para gerar variantes"
                              />
                            </Grid>
                            {values.useAI && (
                              <>
                                <Grid xs={12} md={6} item>
                                  <FormControl
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    className={classes.formControl}
                                  >
                                    <InputLabel id="aiProvider-selection-label">
                                      Provedor de IA
                                    </InputLabel>
                                    <Field
                                      as={Select}
                                      label="Provedor de IA"
                                      labelId="aiProvider-selection-label"
                                      id="aiProvider"
                                      name="aiProvider"
                                      disabled={!campaignEditable}
                                    >
                                      <MenuItem value="groq">Groq</MenuItem>
                                      <MenuItem value="gemini">Google Gemini</MenuItem>
                                      <MenuItem value="deepseek">DeepSeek</MenuItem>
                                      <MenuItem value="openai">OpenAI</MenuItem>
                                    </Field>
                                  </FormControl>
                                </Grid>
                                <Grid xs={12} item>
                                  <Field
                                    as={TextField}
                                    label="Prompt para IA"
                                    name="aiPrompt"
                                    fullWidth
                                    rows={4}
                                    multiline
                                    variant="outlined"
                                    margin="normal"
                                    disabled={!campaignEditable}
                                    helperText="Descreva como a IA deve gerar as 5 variantes de mensagens. Exemplo: 'Gere 5 variantes diferentes de mensagens de boas-vindas, cada uma com um tom diferente (formal, casual, amigável, profissional, criativo)'"
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid xs={12} item>
                      <Tabs
                        value={messageTab}
                        indicatorColor="primary"
                        textColor="primary"
                        onChange={(e, v) => setMessageTab(v)}
                        variant="fullWidth"
                        centered
                      >
                        <Tab label="Msg. 1" />
                        <Tab label="Msg. 2" />
                        <Tab label="Msg. 3" />
                        <Tab label="Msg. 4" />
                        <Tab label="Msg. 5" />
                      </Tabs>
                    </Grid>

                    <Grid xs={12} item>
                      {messageTab === 0 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                {renderMessageField("message1")}
                              </Grid>
                              <Grid xs={12} md={4} item>
                                {renderConfirmationMessageField("confirmationMessage1")}
                              </Grid>
                            </Grid>
                          ) : (
                            renderMessageField("message1")
                          )}
                        </>
                      )}
                      {messageTab === 1 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                {renderMessageField("message2")}
                              </Grid>
                              <Grid xs={12} md={4} item>
                                {renderConfirmationMessageField("confirmationMessage2")}
                              </Grid>
                            </Grid>
                          ) : (
                            renderMessageField("message2")
                          )}
                        </>
                      )}
                      {messageTab === 2 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                {renderMessageField("message3")}
                              </Grid>
                              <Grid xs={12} md={4} item>
                                {renderConfirmationMessageField("confirmationMessage3")}
                              </Grid>
                            </Grid>
                          ) : (
                            renderMessageField("message3")
                          )}
                        </>
                      )}
                      {messageTab === 3 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                {renderMessageField("message4")}
                              </Grid>
                              <Grid xs={12} md={4} item>
                                {renderConfirmationMessageField("confirmationMessage4")}
                              </Grid>
                            </Grid>
                          ) : (
                            renderMessageField("message4")
                          )}
                        </>
                      )}
                      {messageTab === 4 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                {renderMessageField("message5")}
                              </Grid>
                              <Grid xs={12} md={4} item>
                                {renderConfirmationMessageField("confirmationMessage5")}
                              </Grid>
                            </Grid>
                          ) : (
                            renderMessageField("message5")
                          )}
                        </>
                      )}
                    </Grid>



                    <Grid xs={12} md={4} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="openTicket-selection-label">
                          {i18n.t("campaigns.dialog.form.openTicket")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("campaigns.dialog.form.openTicket")}
                          placeholder={i18n.t("campaigns.dialog.form.openTicket")}
                          labelId="openTicket-selection-label"
                          id="openTicket"
                          name="openTicket"
                          error={touched.openTicket && Boolean(errors.openTicket)}
                          disabled={!campaignEditable}
                        >
                          <MenuItem value={"enabled"}>{i18n.t("campaigns.dialog.form.enabledOpenTicket")}</MenuItem>
                          <MenuItem value={"disabled"}>{i18n.t("campaigns.dialog.form.disabledOpenTicket")}</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} md={4} item>
                      <Autocomplete
                        style={{ marginTop: '8px' }}
                        variant="outlined"
                        margin="dense"
                        className={classes.formControl}
                        getOptionLabel={(option) => `${option.name}`}
                        value={selectedUser}
                        size="small"
                        onChange={(e, newValue) => {
                          setSelectedUser(newValue);
                          if (newValue != null && Array.isArray(newValue.queues)) {
                            if (newValue.queues.length === 1) {
                              setSelectedQueue(newValue.queues[0].id);
                            }
                            setQueues(newValue.queues);
                          } else {
                            setQueues(allQueues);
                            setSelectedQueue("");
                          }
                        }}
                        options={options}
                        filterOptions={filterOptions}
                        freeSolo
                        fullWidth
                        autoHighlight
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                        noOptionsText={i18n.t("transferTicketModal.noOptions")}
                        loading={loading}
                        renderOption={option => (
                          <span> <UserStatusIcon user={option} /> {option.name}</span>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={i18n.t("transferTicketModal.fieldLabel")}
                            variant="outlined"
                            onChange={(e) => setSearchParam(e.target.value)}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid xs={12} md={4} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel>
                          {i18n.t("transferTicketModal.fieldQueueLabel")}
                        </InputLabel>
                        <Select
                          value={selectedQueue}
                          onChange={(e) => setSelectedQueue(e.target.value)}
                          label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                          required={!isNil(selectedUser)}
                          disabled={!campaignEditable || values.openTicket === 'disabled'}
                        >
                          {queues.map((queue) => (
                            <MenuItem key={queue.id} value={queue.id}>
                              {queue.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} md={4} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="statusTicket-selection-label">
                          {i18n.t("campaigns.dialog.form.statusTicket")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("campaigns.dialog.form.statusTicket")}
                          placeholder={i18n.t("campaigns.dialog.form.statusTicket")}
                          labelId="statusTicket-selection-label"
                          id="statusTicket"
                          name="statusTicket"
                          error={touched.statusTicket && Boolean(errors.statusTicket)}
                          disabled={!campaignEditable || values.openTicket === 'disabled'}
                        >
                          <MenuItem value={"closed"}>{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MenuItem>
                          <MenuItem value={"pending"}>{i18n.t("campaigns.dialog.form.pendingTicketStatus")}</MenuItem>
                          <MenuItem value={"open"}>{i18n.t("campaigns.dialog.form.openTicketStatus")}</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  {campaign.status === "CANCELADA" && (
                    <Button
                      color="primary"
                      onClick={() => restartCampaign()}
                      variant="outlined"
                    >
                      {i18n.t("campaigns.dialog.buttons.restart")}
                    </Button>
                  )}
                  {campaign.status === "EM_ANDAMENTO" && (
                    <Button
                      color="primary"
                      onClick={() => cancelCampaign()}
                      variant="outlined"
                    >
                      {i18n.t("campaigns.dialog.buttons.cancel")}
                    </Button>
                  )}

                  <Button
                    onClick={handleClose}
                    color="primary"
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.close")}
                  </Button>
                  {(campaignEditable || campaign.status === "CANCELADA") && (
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                      variant="contained"
                      className={classes.btnWrapper}
                    >
                      {campaignId
                        ? `${i18n.t("campaigns.dialog.buttons.edit")}`
                        : `${i18n.t("campaigns.dialog.buttons.add")}`}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  )}
                </DialogActions>
              </Form>
            </>
          )}
        </Formik>
      </Dialog>
      {isPage && (
        <Formik
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <div style={{ display: "none" }}>
                <input
                  type="file"
                  ref={attachmentFile}
                  onChange={(e) => handleAttachmentFile(e)}
                />
              </div>
              <Grid spacing={2} container style={{ marginBottom: "24px" }}>
                {values.apiType && (
                  <Grid xs={12} item>
                    <Box style={{
                      background: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#dbeafe" : "#fee2e2",
                      border: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "2px solid #0284c7" : "2px solid #dc2626",
                      borderRadius: "8px",
                      padding: "20px",
                      marginBottom: "16px"
                    }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography style={{ 
                          color: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#0c4a6e" : "#991b1b", 
                          fontWeight: 700, 
                          fontSize: "16px" 
                        }}>
                          {(values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "✅ API OFICIAL META" : "⚠️ API NÃO OFICIAL"}
                        </Typography>
                        <Box style={{
                          background: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#0284c7" : "#dc2626",
                          color: "white",
                          padding: "8px 16px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 600
                        }}>
                          {values.apiType === "uzap-medium" && "Uzap API - Médio Risco"}
                          {values.apiType === "official-notification-hub" && "Notification Hub"}
                          {values.apiType === "official-waba" && "WABA"}
                          {values.apiType === "humanized" && "Campanha Humanizada"}
                          {values.apiType === "medium" && "Campanha Médio Risco"}
                          {values.apiType === "high" && "Campanha Alto Risco"}
                        </Box>
                      </Box>
                      
                      <Typography style={{ 
                        color: (values.apiType === "official-notification-hub" || values.apiType === "official-waba") ? "#164e63" : "#7f1d1d", 
                        fontSize: "13px", 
                        lineHeight: "1.6" 
                      }}>
                        {values.apiType === "uzap-medium" && "Você está usando a Uzap API (Não-Oficial). Existe risco de bloqueio e perda do número de WhatsApp. Use com cautela e sempre respeite os limites recomendados."}
                        {values.apiType === "humanized" && "Você está usando a Campanha Humanizada (Não-Oficial). Envios lentos e naturais para evitar bloqueios. Ideal para campanhas de relacionamento."}
                        {values.apiType === "medium" && "Você está usando a Campanha Médio Risco (Não-Oficial). Envios moderados com bom alcance. Equilibra velocidade e segurança."}
                        {values.apiType === "high" && "Você está usando a Campanha Alto Risco (Não-Oficial). Envios rápidos para máximo alcance. Maior velocidade, maior risco de bloqueio."}
                        {values.apiType === "official-notification-hub" && "Você está usando a WhatsApp API Oficial Meta via Notification Hub. Esta é a forma mais segura e confiável. Você será cobrado por mensagem enviada."}
                        {values.apiType === "official-waba" && "Você está usando a WhatsApp API Oficial Meta via WABA. Esta é a forma mais segura e confiável. Você será cobrado por mensagem enviada."}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid xs={12} md={6} item>
                  <Field
                    as={TextField}
                    label={i18n.t("campaigns.dialog.form.name")}
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.textField}
                    disabled={!campaignEditable}
                  />
                </Grid>
                
                <Grid xs={12} md={6} item>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.formControl}
                  >
                    <InputLabel id="confirmation-selection-label">
                      {i18n.t("campaigns.dialog.form.confirmation")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("campaigns.dialog.form.confirmation")}
                      placeholder={i18n.t("campaigns.dialog.form.confirmation")}
                      labelId="confirmation-selection-label"
                      id="confirmation"
                      name="confirmation"
                      error={touched.confirmation && Boolean(errors.confirmation)}
                      disabled={!campaignEditable}
                    >
                      <MenuItem value={false}>Desabilitada</MenuItem>
                      <MenuItem value={true}>Habilitada</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={6} item>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.formControl}
                  >
                    <InputLabel id="contactList-selection-label">
                      {i18n.t("campaigns.dialog.form.contactList")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("campaigns.dialog.form.contactList")}
                      placeholder={i18n.t("campaigns.dialog.form.contactList")}
                      labelId="contactList-selection-label"
                      id="contactListId"
                      name="contactListId"
                      error={touched.contactListId && Boolean(errors.contactListId)}
                      disabled={!campaignEditable}
                    >
                      <MenuItem value="">Nenhuma</MenuItem>
                      {contactLists &&
                        contactLists.map((contactList) => (
                          <MenuItem key={contactList.id} value={contactList.id}>
                            {contactList.name}
                          </MenuItem>
                        ))}
                    </Field>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={6} item>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.formControl}
                  >
                    <InputLabel id="tagList-selection-label">
                      {i18n.t("campaigns.dialog.form.tagList")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("campaigns.dialog.form.tagList")}
                      placeholder={i18n.t("campaigns.dialog.form.tagList")}
                      labelId="tagList-selection-label"
                      id="tagListId"
                      name="tagListId"
                      error={touched.tagListId && Boolean(errors.tagListId)}
                      disabled={!campaignEditable}
                    >
                      {Array.isArray(tagLists) &&
                        tagLists.map((tagList) => (
                          <MenuItem key={tagList.id} value={tagList.id}>
                            {tagList.name}
                          </MenuItem>
                        ))}
                    </Field>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={6} item>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.formControl}
                  >
                    <InputLabel id="whatsapp-selection-label">
                      {i18n.t("campaigns.dialog.form.whatsapp")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("campaigns.dialog.form.whatsapp")}
                      placeholder={i18n.t("campaigns.dialog.form.whatsapp")}
                      labelId="whatsapp-selection-label"
                      id="whatsappIds"
                      name="whatsappIds"
                      required
                      error={touched.whatsappId && Boolean(errors.whatsappId)}
                      disabled={!campaignEditable}
                      value={whatsappId}
                      onChange={(event) => {
                        setWhatsappId(event.target.value);
                        setFieldValue('whatsappId', event.target.value);
                      }}
                    >
                      {whatsapps &&
                        whatsapps.map((whatsapp) => (
                          <MenuItem key={whatsapp.id} value={whatsapp.id}>
                            {whatsapp.name}
                          </MenuItem>
                        ))}
                    </Field>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={6} item>
                  <Field
                    as={TextField}
                    label={i18n.t("campaigns.dialog.form.scheduledAt")}
                    name="scheduledAt"
                    error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                    helperText={touched.scheduledAt && errors.scheduledAt}
                    variant="outlined"
                    margin="dense"
                    type="datetime-local"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    className={classes.textField}
                    disabled={!campaignEditable}
                  />
                </Grid>

                <Grid xs={12} item>
                  <Card className={classes.recurrenceCard}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <RepeatIcon className={classes.recurrenceIcon} />
                        <Typography variant="h6">Configuração de Recorrência</Typography>
                      </Box>
                      <Grid spacing={2} container>
                        <Grid xs={12} item>
                          <FormControlLabel
                            control={
                              <Field
                                as={Switch}
                                name="isRecurring"
                                color="primary"
                                disabled={!campaignEditable}
                              />
                            }
                            label="Habilitar recorrência"
                          />
                        </Grid>
                        {values.isRecurring && (
                          <>
                            <Grid xs={12} md={6} item>
                              <FormControl
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                className={classes.formControl}
                              >
                                <InputLabel id="recurrenceType-selection-label">
                                  Tipo de Recorrência
                                </InputLabel>
                                <Field
                                  as={Select}
                                  label="Tipo de Recorrência"
                                  labelId="recurrenceType-selection-label"
                                  id="recurrenceType"
                                  name="recurrenceType"
                                  error={touched.recurrenceType && Boolean(errors.recurrenceType)}
                                  disabled={!campaignEditable}
                                >
                                  <MenuItem value="minutely">A cada minuto</MenuItem>
                                  <MenuItem value="hourly">A cada hora</MenuItem>
                                  <MenuItem value="daily">Diariamente</MenuItem>
                                  <MenuItem value="weekly">Semanalmente</MenuItem>
                                  <MenuItem value="biweekly">A cada 2 semanas</MenuItem>
                                  <MenuItem value="monthly">Mensalmente</MenuItem>
                                  <MenuItem value="yearly">Anualmente</MenuItem>
                                </Field>
                              </FormControl>
                            </Grid>
                            <Grid xs={12} md={6} item>
                              <Field
                                as={TextField}
                                label="Intervalo"
                                name="recurrenceInterval"
                                type="number"
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                disabled={!campaignEditable}
                                helperText="A cada X unidades"
                              />
                            </Grid>
                            {values.recurrenceType === 'weekly' && (
                              <Grid xs={12} item>
                                <FormGroup row>
                                  {daysOfWeekOptions.map((day) => (
                                    <FormControlLabel
                                      key={day.value}
                                      control={
                                        <Checkbox
                                          checked={values.recurrenceDaysOfWeek.includes(day.value)}
                                          onChange={(e) => {
                                            const newDays = e.target.checked
                                              ? [...values.recurrenceDaysOfWeek, day.value]
                                              : values.recurrenceDaysOfWeek.filter(d => d !== day.value);
                                            setFieldValue('recurrenceDaysOfWeek', newDays);
                                          }}
                                          disabled={!campaignEditable}
                                        />
                                      }
                                      label={day.label}
                                    />
                                  ))}
                                </FormGroup>
                              </Grid>
                            )}
                            {values.recurrenceType === 'monthly' && (
                              <Grid xs={12} md={6} item>
                                <Field
                                  as={TextField}
                                  label="Dia do mês"
                                  name="recurrenceDayOfMonth"
                                  type="number"
                                  variant="outlined"
                                  margin="normal"
                                  fullWidth
                                  disabled={!campaignEditable}
                                  inputProps={{ min: 1, max: 31 }}
                                />
                              </Grid>
                            )}
                            <Grid xs={12} md={6} item>
                              <Field
                                as={TextField}
                                label="Data Final (opcional)"
                                name="recurrenceEndDate"
                                type="date"
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                disabled={!campaignEditable}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>
                            <Grid xs={12} md={6} item>
                              <Field
                                as={TextField}
                                label="Máximo de Execuções (opcional)"
                                name="maxExecutions"
                                type="number"
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                disabled={!campaignEditable}
                                helperText="Deixe vazio para recorrência infinita"
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid xs={12} item>
                  <Card className={classes.recurrenceCard}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Typography variant="h6">⚙️ Configuração de Campanha</Typography>
                      </Box>
                      <Box style={{
                        background: "#fef3c7",
                        border: "1px solid #fcd34d",
                        borderRadius: "6px",
                        padding: "12px",
                        marginBottom: "16px"
                      }}>
                        <Typography style={{ fontSize: "13px", color: "#92400e", lineHeight: "1.5" }}>
                          💡 <strong>Dica:</strong> Se o número de contatos exceder o limite configurado, a campanha será automaticamente dividida em múltiplas campanhas para respeitar os limites de segurança.
                        </Typography>
                      </Box>
                      <Grid spacing={2} container>
                        {values.maxContacts && (
                          <Grid xs={12} md={4} item>
                            <Field
                              as={TextField}
                              label="Máximo de Contatos"
                              name="maxContacts"
                              type="number"
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              disabled={!campaignEditable}
                            />
                          </Grid>
                        )}
                        {values.intervalBetweenMessages && (
                          <Grid xs={12} md={4} item>
                            <Field
                              as={TextField}
                              label="Intervalo entre Mensagens (ms)"
                              name="intervalBetweenMessages"
                              type="number"
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              disabled={!campaignEditable}
                            />
                          </Grid>
                        )}
                        {values.delayBetweenContacts && (
                          <Grid xs={12} md={4} item>
                            <Field
                              as={TextField}
                              label="Delay entre Contatos (ms)"
                              name="delayBetweenContacts"
                              type="number"
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              disabled={!campaignEditable}
                            />
                          </Grid>
                        )}
                        <Grid xs={12} md={4} item>
                          <Field
                            as={TextField}
                            label="Delay Inicial (segundos)"
                            name="initialDelay"
                            type="number"
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            disabled={!campaignEditable}
                            helperText="Aguarde antes de iniciar"
                            inputProps={{ min: 0, step: 1 }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid xs={12} md={4} item>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.formControl}
                  >
                    <InputLabel id="openTicket-selection-label">
                      {i18n.t("campaigns.dialog.form.openTicket")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("campaigns.dialog.form.openTicket")}
                      placeholder={i18n.t("campaigns.dialog.form.openTicket")}
                      labelId="openTicket-selection-label"
                      id="openTicket"
                      name="openTicket"
                      error={touched.openTicket && Boolean(errors.openTicket)}
                      disabled={!campaignEditable}
                    >
                      <MenuItem value={"enabled"}>{i18n.t("campaigns.dialog.form.enabledOpenTicket")}</MenuItem>
                      <MenuItem value={"disabled"}>{i18n.t("campaigns.dialog.form.disabledOpenTicket")}</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={4} item>
                  <Autocomplete
                    style={{ marginTop: '8px' }}
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                    getOptionLabel={(option) => `${option.name}`}
                    value={selectedUser}
                    size="small"
                    onChange={(e, newValue) => {
                      setSelectedUser(newValue);
                      if (newValue != null && Array.isArray(newValue.queues)) {
                        if (newValue.queues.length === 1) {
                          setSelectedQueue(newValue.queues[0].id);
                        }
                        setQueues(newValue.queues);
                      } else {
                        setQueues(allQueues);
                        setSelectedQueue("");
                      }
                    }}
                    options={options}
                    filterOptions={filterOptions}
                    freeSolo
                    fullWidth
                    autoHighlight
                    disabled={!campaignEditable || values.openTicket === 'disabled'}
                    noOptionsText={i18n.t("transferTicketModal.noOptions")}
                    loading={loading}
                    renderOption={option => (
                      <span> <UserStatusIcon user={option} /> {option.name}</span>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={i18n.t("transferTicketModal.fieldLabel")}
                        variant="outlined"
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid xs={12} md={4} item>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.formControl}
                  >
                    <InputLabel>
                      {i18n.t("transferTicketModal.fieldQueueLabel")}
                    </InputLabel>
                    <Select
                      value={selectedQueue}
                      onChange={(e) => setSelectedQueue(e.target.value)}
                      label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                      required={!isNil(selectedUser)}
                      disabled={!campaignEditable || values.openTicket === 'disabled'}
                    >
                      {queues.map((queue) => (
                        <MenuItem key={queue.id} value={queue.id}>
                          {queue.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={4} item>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    className={classes.formControl}
                  >
                    <InputLabel id="statusTicket-selection-label">
                      {i18n.t("campaigns.dialog.form.statusTicket")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("campaigns.dialog.form.statusTicket")}
                      placeholder={i18n.t("campaigns.dialog.form.statusTicket")}
                      labelId="statusTicket-selection-label"
                      id="statusTicket"
                      name="statusTicket"
                      error={touched.statusTicket && Boolean(errors.statusTicket)}
                      disabled={!campaignEditable || values.openTicket === 'disabled'}
                    >
                      <MenuItem value={"closed"}>{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MenuItem>
                      <MenuItem value={"pending"}>{i18n.t("campaigns.dialog.form.pendingTicketStatus")}</MenuItem>
                      <MenuItem value={"open"}>{i18n.t("campaigns.dialog.form.openTicketStatus")}</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>

                <Grid xs={12} item>
                  <Card className={classes.recurrenceCard}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Typography variant="h6">🤖 Usar Inteligência Artificial</Typography>
                      </Box>
                      <Grid spacing={2} container>
                        <Grid xs={12} md={6} item>
                          <FormControlLabel
                            control={
                              <Field
                                as={Switch}
                                name="useAI"
                                color="primary"
                                disabled={!campaignEditable}
                              />
                            }
                            label="Habilitar IA para gerar variantes"
                          />
                        </Grid>
                        {values.useAI && (
                          <>
                            <Grid xs={12} md={6} item>
                              <FormControl
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                className={classes.formControl}
                              >
                                <InputLabel id="aiProvider-selection-label">
                                  Provedor de IA
                                </InputLabel>
                                <Field
                                  as={Select}
                                  label="Provedor de IA"
                                  labelId="aiProvider-selection-label"
                                  id="aiProvider"
                                  name="aiProvider"
                                  disabled={!campaignEditable}
                                >
                                  <MenuItem value="groq">Groq</MenuItem>
                                  <MenuItem value="gemini">Google Gemini</MenuItem>
                                  <MenuItem value="deepseek">DeepSeek</MenuItem>
                                  <MenuItem value="openai">OpenAI</MenuItem>
                                </Field>
                              </FormControl>
                            </Grid>
                            <Grid xs={12} item>
                              <Field
                                as={TextField}
                                label="Prompt para IA"
                                name="aiPrompt"
                                fullWidth
                                rows={4}
                                multiline
                                variant="outlined"
                                margin="normal"
                                disabled={!campaignEditable}
                                helperText="Descreva como a IA deve gerar as 5 variantes de mensagens. Exemplo: 'Gere 5 variantes diferentes de mensagens de boas-vindas, cada uma com um tom diferente (formal, casual, amigável, profissional, criativo)'"
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid xs={12} item>
                  <Tabs
                    value={messageTab}
                    indicatorColor="primary"
                    textColor="primary"
                    onChange={(e, v) => setMessageTab(v)}
                    variant="fullWidth"
                    centered
                  >
                    <Tab label="Msg. 1" />
                    <Tab label="Msg. 2" />
                    <Tab label="Msg. 3" />
                    <Tab label="Msg. 4" />
                    <Tab label="Msg. 5" />
                  </Tabs>
                </Grid>

              <Grid xs={12} item>
                  {messageTab === 0 && (
                    <>
                      {values.confirmation ? (
                        <Grid spacing={2} container>
                          <Grid xs={12} md={8} item>
                            {renderMessageField("message1")}
                          </Grid>
                          <Grid xs={12} md={4} item>
                            {renderConfirmationMessageField("confirmationMessage1")}
                          </Grid>
                        </Grid>
                      ) : (
                        renderMessageField("message1")
                      )}
                    </>
                  )}
                  {messageTab === 1 && (
                    <>
                      {values.confirmation ? (
                        <Grid spacing={2} container>
                          <Grid xs={12} md={8} item>
                            {renderMessageField("message2")}
                          </Grid>
                          <Grid xs={12} md={4} item>
                            {renderConfirmationMessageField("confirmationMessage2")}
                          </Grid>
                        </Grid>
                      ) : (
                        renderMessageField("message2")
                      )}
                    </>
                  )}
                  {messageTab === 2 && (
                    <>
                      {values.confirmation ? (
                        <Grid spacing={2} container>
                          <Grid xs={12} md={8} item>
                            {renderMessageField("message3")}
                          </Grid>
                          <Grid xs={12} md={4} item>
                            {renderConfirmationMessageField("confirmationMessage3")}
                          </Grid>
                        </Grid>
                      ) : (
                        renderMessageField("message3")
                      )}
                    </>
                  )}
                  {messageTab === 3 && (
                    <>
                      {values.confirmation ? (
                        <Grid spacing={2} container>
                          <Grid xs={12} md={8} item>
                            {renderMessageField("message4")}
                          </Grid>
                          <Grid xs={12} md={4} item>
                            {renderConfirmationMessageField("confirmationMessage4")}
                          </Grid>
                        </Grid>
                      ) : (
                        renderMessageField("message4")
                      )}
                    </>
                  )}
                  {messageTab === 4 && (
                    <>
                      {values.confirmation ? (
                        <Grid spacing={2} container>
                          <Grid xs={12} md={8} item>
                            {renderMessageField("message5")}
                          </Grid>
                          <Grid xs={12} md={4} item>
                            {renderConfirmationMessageField("confirmationMessage5")}
                          </Grid>
                        </Grid>
                      ) : (
                        renderMessageField("message5")
                      )}
                    </>
                  )}
                  {isPage && onPreviewUpdate && (
                    <PreviewUpdater 
                      values={values} 
                      onPreviewUpdate={onPreviewUpdate} 
                    />
                  )}
                </Grid>
              </Grid>

              <Box mt={3} display="flex" gap={2} justifyContent="flex-end">
                <Button
                  onClick={() => {
                    try {
                      handleClose();
                    } catch (err) {
                      console.error('Erro ao cancelar:', err);
                    }
                  }}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {campaignId
                    ? `${i18n.t("campaigns.dialog.buttons.edit")}`
                    : `${i18n.t("campaigns.dialog.buttons.add")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default CampaignModal;
